import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Bed, Bath, MapPin, Wifi, Loader2, Home, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public property showcase — unauthenticated, gallery + basic info.
 * Rendered at /p/:slug. Deliberately does NOT import Sidebar or any
 * auth-aware components so operators can share this URL freely.
 */
export default function PublicPropertyPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/properties/${slug}`);
        if (!cancelled) {
          setData(res.data);
          setStatus('ready');
        }
      } catch (err) {
        if (!cancelled) {
          setStatus(err.response?.status === 404 ? 'not-found' : 'error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (status === 'not-found') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <Home className="h-10 w-10 text-slate-400 mb-3" />
        <h1 className="text-2xl font-semibold text-slate-800 mb-2">Property not found</h1>
        <p className="text-slate-500 mb-6">This showcase page is no longer available.</p>
        <Link
          to="/"
          className="text-sm underline underline-offset-4 text-slate-700"
        >
          Back to PROP VEIW
        </Link>
      </div>
    );
  }
  if (status === 'error' || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
        <p className="text-red-600">We couldn&apos;t load this showcase. Please try again shortly.</p>
      </div>
    );
  }

  const photos = data.photos?.filter((p) => p.url) || [];
  const heroImage = data.hero_image || photos[0]?.url;

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const shiftLightbox = (delta) => {
    if (lightboxIndex == null) return;
    const next = (lightboxIndex + delta + photos.length) % photos.length;
    setLightboxIndex(next);
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="public-property-page">
      {/* Slim top bar */}
      <header className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-slate-900 flex items-center justify-center">
              <Home className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-semibold text-slate-900">PROP VEIW</span>
          </Link>
          <a
            href="https://propveiw.com"
            className="text-xs text-slate-500 hover:text-slate-800"
          >
            Powered by PROP VEIW
          </a>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative w-full bg-slate-100 overflow-hidden"
        data-testid="public-property-hero"
      >
        {heroImage ? (
          <img
            src={heroImage}
            alt={data.name}
            className="w-full h-[280px] sm:h-[440px] object-cover"
          />
        ) : (
          <div className="w-full h-[220px] flex items-center justify-center text-slate-400">
            <Home className="h-12 w-12" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60 pointer-events-none" />
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8 text-white">
          <div className="max-w-6xl mx-auto">
            <h1 className="font-manrope text-3xl sm:text-5xl font-bold drop-shadow" data-testid="public-property-name">
              {data.name}
            </h1>
            {data.headline && (
              <p className="text-lg text-white/90 mt-2 drop-shadow" data-testid="public-property-headline">
                {data.headline}
              </p>
            )}
            {data.location && (
              <p className="text-sm text-white/80 mt-2 flex items-center gap-1">
                <MapPin className="h-4 w-4" /> {data.location}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Highlights row */}
      <section className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-6 text-sm text-slate-700">
          <div className="flex items-center gap-1.5">
            <Bed className="h-4 w-4 text-slate-500" />
            <span>{data.bedrooms} bed{data.bedrooms === 1 ? '' : 's'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Bath className="h-4 w-4 text-slate-500" />
            <span>{data.bathrooms} bath{data.bathrooms === 1 ? '' : 's'}</span>
          </div>
          {data.amenities?.slice(0, 2).map((a) => (
            <div key={a} className="hidden sm:flex items-center gap-1.5 text-slate-500">
              <Wifi className="h-3.5 w-3.5" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Content grid */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          {data.description && (
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-3">About this property</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {data.description}
              </p>
            </section>
          )}

          {photos.length > 0 && (
            <section data-testid="public-property-gallery">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">Photo gallery</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((p, i) => (
                  <button
                    key={`${p.url}-${i}`}
                    onClick={() => openLightbox(i)}
                    className="group relative aspect-square overflow-hidden rounded-lg bg-slate-100"
                    data-testid={`public-photo-${i}`}
                  >
                    <img
                      src={p.url}
                      alt={p.caption || `${data.name} photo ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                      loading="lazy"
                    />
                    {p.caption && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent text-[11px] text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {p.caption}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-6">
          {data.amenities?.length > 0 && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-3">Amenities</h2>
              <ul className="grid grid-cols-1 gap-2 text-sm text-slate-700">
                {data.amenities.map((a) => (
                  <li key={a} className="flex items-center gap-2 border rounded-md px-3 py-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {a}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="border rounded-lg p-4 bg-slate-50">
            <p className="text-sm text-slate-600 mb-3">
              Interested in booking or asking a question?
            </p>
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="w-full"
              data-testid="public-print-btn"
            >
              Print listing
            </Button>
            <p className="text-[11px] text-slate-500 mt-3">
              Contact your property manager for booking availability and rates.
            </p>
          </section>
        </aside>
      </main>

      <footer className="border-t bg-slate-50 py-6 text-center text-xs text-slate-500">
        {data.name} · Managed via{' '}
        <Link to="/" className="underline underline-offset-4 hover:text-slate-800">
          PROP VEIW
        </Link>
      </footer>

      {/* Lightbox */}
      {lightboxIndex != null && photos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="public-lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 text-white/90 hover:text-white"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          {photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); shiftLightbox(-1); }}
                className="absolute left-2 sm:left-6 p-2 text-white/80 hover:text-white"
                aria-label="Previous"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); shiftLightbox(1); }}
                className="absolute right-2 sm:right-6 p-2 text-white/80 hover:text-white"
                aria-label="Next"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}
          <img
            src={photos[lightboxIndex].url}
            alt={photos[lightboxIndex].caption || 'Photo'}
            className="max-h-[90vh] max-w-[92vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
